// A training sample in the list (lib/train/clips): its waveform with the
// voice in purple and the silence we removed in grey, and a play button that
// plays only what the voice will learn from. Silence is removed
// automatically; Trim opens the file in ClipEditor to cut more by hand.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Pause, Play } from "lucide-react";
import { useT } from "@jammable/ui-core/src/i18n";
import type { Clip, Range } from "@/lib/train/clips";

const inside = (list: Range[], t: number) =>
  list.some(([s, e]) => t >= s && t < e);

/** The file's waveform: voice in purple, removed silence in grey. */
export function SampleWave({ clip, keep }: { clip: Clip; keep: Range[] }) {
  const a = clip.analysis!;
  return (
    <div
      className="tw-flex tw-h-12 tw-items-center tw-gap-[2px] tw-rounded-[18px] tw-bg-jam-lav tw-px-3"
      aria-hidden="true"
    >
      {a.peaks
        .filter((_, i) => i % 2 === 0)
        .map((p, i, arr) => {
          const t = ((i + 0.5) / arr.length) * a.duration;
          return (
            <i
              key={i}
              className={`tw-block tw-flex-1 tw-rounded-full ${inside(keep, t) ? "tw-bg-jam-purple" : "tw-bg-jam-ink/15"}`}
              style={{ height: `${12 + p * 70}%` }}
            />
          );
        })}
    </div>
  );
}

// One thing plays at a time on the page (a sample's play button, the trim
// editor): whoever starts playing stops whatever was playing.
const nowPlaying: { stop: (() => void) | null } = { stop: null };
export function takeOverPlayback(stop: () => void) {
  if (nowPlaying.stop && nowPlaying.stop !== stop) nowPlaying.stop();
  nowPlaying.stop = stop;
}

/** Plays what's kept of the file, skipping the silence. */
export function PlayKept({
  file,
  keep,
  label,
}: {
  file: Blob;
  keep: Range[];
  label: string;
}) {
  const t = useT();
  const url = useMemo(() => URL.createObjectURL(file), [file]);
  const audio = useRef<HTMLAudioElement | null>(null);
  const raf = useRef(0);
  const [playing, setPlaying] = useState(false);
  const stop = () => {
    cancelAnimationFrame(raf.current);
    audio.current?.pause();
    setPlaying(false);
  };
  useEffect(
    () => () => {
      cancelAnimationFrame(raf.current);
      audio.current?.pause();
      URL.revokeObjectURL(url);
    },
    [url],
  );
  const play = async () => {
    const a = (audio.current ??= new Audio(url));
    if (!keep.length) return;
    // Before playing, so an older stop of ours can't pause what starts.
    takeOverPlayback(stop);
    if (!inside(keep, a.currentTime) || a.ended) a.currentTime = keep[0][0];
    try {
      await a.play();
    } catch {
      return;
    }
    setPlaying(true);
    const tick = () => {
      if (a.paused) return stop();
      if (!inside(keep, a.currentTime)) {
        const next = keep.find(([s]) => s > a.currentTime);
        if (!next) {
          a.currentTime = keep[0][0];
          return stop();
        }
        a.currentTime = next[0];
      }
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
  };
  return (
    <button
      type="button"
      onClick={() => (playing ? stop() : play())}
      aria-label={t(playing ? "train.sample.pause" : "train.sample.play", {
        label,
      })}
      className="tw-flex tw-h-11 tw-w-11 tw-flex-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-jam-purple tw-text-white tw-transition-transform hover:tw-scale-105"
    >
      {playing ? (
        <Pause size={17} fill="currentColor" strokeWidth={0} />
      ) : (
        <Play size={17} fill="currentColor" strokeWidth={0} />
      )}
    </button>
  );
}
